import { rpeToRir, parseLocaleNumber } from '@heraion/shared';

/** Canonical RIR from a set's two effort text fields — the ONE resolver.
 *
 *  `rir` wins when present; a legacy `rpe` is projected. Returns null when
 *  neither parses.
 *
 *  ⚠️ `parseLocaleNumber`, never `Number`: the family types on it-IT / fr-FR /
 *  de-DE keyboards, where a half-step effort is written "8,5". `Number("8,5")`
 *  is NaN, so a hand-rolled parser here silently answers "no effort recorded"
 *  for a value that is plainly there — and every caller of this function acts
 *  on that answer (what the meter shows, what a migration decides to move).
 *  That is exactly the divergence two copies of this logic produced. */
export const canonicalRirOf = (
  rir: string | undefined,
  rpe: string | undefined,
): number | null =>
  canonicalRirFromNumbers(
    numOrUndefined(parseLocaleNumber(rir ?? '')),
    numOrUndefined(parseLocaleNumber(rpe ?? '')),
  );

const numOrUndefined = (n: number): number | undefined =>
  Number.isFinite(n) ? n : undefined;

/** The same rule when the two values arrive already parsed — the hydration
 *  paths read them off a stored/prescribed set, where they are numbers.
 *  Same precedence, same rounding: one rule, two doors. */
export const canonicalRirFromNumbers = (
  rir: number | undefined,
  rpe: number | undefined,
): number | null => {
  if (rir !== undefined && Number.isFinite(rir)) return Math.round(rir);
  if (rpe !== undefined && Number.isFinite(rpe)) return Math.round(rpeToRir(rpe));
  return null;
};
